import React, { useEffect, useMemo, useRef, useState } from "react";
import apiService from "../../services/apiService";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import "../Stock/StockList.css";

const ROLE = {
  MA: 1,
  SM: 2,
  ACC: 8,
  PMG: 9,
};

const safeParseUser = (storageKey) => {
  try {
    return JSON.parse(storageKey || "{}");
  } catch {
    return {};
  }
};

const getRoleId = () => {
  const sessionUser = safeParseUser(sessionStorage.getItem("user"));
  const localUser = safeParseUser(localStorage.getItem("user"));

  const u = Object.keys(sessionUser).length ? sessionUser : localUser;

  return Number(
    u.RoleID ??
    u.roleId ??
    u.RoleId ??
    u.roleID ??
    u.role_id ??
    0
  );
};

const canSeeExpiryColumns = (roleId) => {
  const id = Number(roleId);

  if (id === ROLE.SM) return false;
  if (id === ROLE.ACC) return false;
  if (id === ROLE.MA) return false;

  return true;
};

const toDateOnly = (d) => {
  if (!d) return null;
  const s = String(d).slice(0, 10);
  return new Date(`${s}T00:00:00`);
};

const todayDateOnly = () => {
  const now = new Date();
  return new Date(`${now.toISOString().slice(0, 10)}T00:00:00`);
};

const getDaysRemainingPure = (expiryDate) => {
  const exp = toDateOnly(expiryDate);
  if (!exp) return "";
  const t = todayDateOnly();
  return Math.ceil((exp - t) / (1000 * 60 * 60 * 24));
};

// const isWithin6Months = (expiryDate) => {
//   const exp = toDateOnly(expiryDate);
//   if (!exp) return false;

//   const t = todayDateOnly();
//   const sixMonthsFromToday = new Date(t);
//   sixMonthsFromToday.setMonth(sixMonthsFromToday.getMonth() + 6);

//   return exp <= sixMonthsFromToday;
// };

const canShowExpiryValue = (roleId, expiryDate) => {
  const id = Number(roleId);

  if (id === ROLE.SM || id === ROLE.ACC || id === ROLE.MA) return false;

  // if (id === ROLE.MA || id === ROLE.PMG) {
  //   return !isWithin6Months(expiryDate);
  // }

  return true;
};

const safeExcelText = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  return String(value);
};

const cleanFileName = (value) => {
  return String(value || "ITEM").replace(/[^\w-]+/g, "_");
};

const getItemKey = (row) =>
  `${String(row?.CatalogNo || "").trim()}||${String(row?.ItemName || "").trim()}`;

export default function StockList() {
  const [items, setItems] = useState([]);
  const [incomingStocks, setIncomingStocks] = useState([]);
  const [suppliers, setSuppliers] = useState([]);

  const [selectedSupplier, setSelectedSupplier] = useState("all");
  const [selectedWarehouse, setSelectedWarehouse] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");

  const [movementFrom, setMovementFrom] = useState("");
  const [movementTo, setMovementTo] = useState("");
  const [movementLotNumber, setMovementLotNumber] = useState("");
  const [movementExporting, setMovementExporting] = useState(false);

  const [supplierDropdownOpen, setSupplierDropdownOpen] = useState(false);
  const [supplierSearch, setSupplierSearch] = useState("");
  const supplierDropdownRef = useRef(null);

  const [showModal, setShowModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  const [details, setDetails] = useState([]);

  const [expandedLotKey, setExpandedLotKey] = useState(null);
  const [lotInfo, setLotInfo] = useState(null);
  const [expandedBox, setExpandedBox] = useState(null);

  const [stockExporting, setStockExporting] = useState(false);
  const [annualPrinting, setAnnualPrinting] = useState(false);

  const [barcodePrintModal, setBarcodePrintModal] = useState({
    open: false,
    labels: [],
    receivedDateFilter: null,
    totalLabels: 0,
    totalBatches: 0,
    loading: false,
  });

  const roleId = getRoleId();
  const showExpiryColumns = canSeeExpiryColumns(roleId);

  const PAGE_SIZE = 10;
  const [page, setPage] = useState(1);

  useEffect(() => {
    loadSuppliers();
    loadItems();
    loadIncomingStocks();
  }, []);

  useEffect(() => {
    let timerId;

    const poll = async () => {
      try {
        await Promise.all([loadItems(), loadIncomingStocks()]);
      } catch (e) {
        console.error("StockList poll load data error:", e);
      } finally {
        timerId = setTimeout(poll, 10000);
      }
    };

    timerId = setTimeout(poll, 10000);

    return () => {
      if (timerId) clearTimeout(timerId);
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        supplierDropdownRef.current &&
        !supplierDropdownRef.current.contains(event.target)
      ) {
        setSupplierDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const loadSuppliers = async () => {
    const data = await apiService.getSuppliers();
    setSuppliers(data || []);
  };

  const loadItems = async () => {
    const data = await apiService.getItemsSummary();
    setItems(data || []);
  };

  const loadIncomingStocks = async () => {
    try {
      if (typeof apiService.getIncomingStocksFromPO !== "function") {
        setIncomingStocks([]);
        return;
      }

      const data = await apiService.getIncomingStocksFromPO();
      setIncomingStocks(data || []);
    } catch (e) {
      console.error("getIncomingStocksFromPO error:", e);
      setIncomingStocks([]);
    }
  };

  const incomingStockMap = useMemo(() => {
    const map = new Map();

    (incomingStocks || []).forEach((row) => {
      const key = getItemKey(row);
      const current = map.get(key) || 0;
      map.set(key, current + Number(row.IncomingQty || 0));
    });

    return map;
  }, [incomingStocks]);

  const itemsWithIncomingStock = useMemo(() => {
    return (items || []).map((it) => ({
      ...it,
      IncomingStock: incomingStockMap.get(getItemKey(it)) || 0,
    }));
  }, [items, incomingStockMap]);

  const getExpiryStatus = (expiryDate) => {
    const diff = getDaysRemainingPure(expiryDate);
    if (diff === "") return "";
    if (diff < 0) return "expired";
    if (diff <= 30) return "soon";
    return "safe";
  };

  const selectedSupplierName = useMemo(() => {
    if (selectedSupplier === "all") return "All Suppliers";

    const selected = suppliers.find(
      (s) => String(s.SupplierID) === String(selectedSupplier)
    );

    return selected?.SupplierName || "All Suppliers";
  }, [selectedSupplier, suppliers]);

  const filteredSupplierOptions = useMemo(() => {
    const term = supplierSearch.toLowerCase().trim();

    if (!term) return suppliers || [];

    return (suppliers || []).filter((s) =>
      String(s.SupplierName || "").toLowerCase().includes(term)
    );
  }, [suppliers, supplierSearch]);

  const warehouseOptions = useMemo(() => {
    const map = new Map();

    (itemsWithIncomingStock || []).forEach((it) => {
      const ids = String(it.WarehouseIDs || "")
        .split(",")
        .map((x) => x.trim())
        .filter(Boolean);

      const names = String(it.WarehouseNames || "")
        .split(",")
        .map((x) => x.trim())
        .filter(Boolean);

      ids.forEach((id, index) => {
        if (!map.has(id)) {
          map.set(id, names[index] || `Warehouse ${id}`);
        }
      });
    });

    return Array.from(map.entries())
      .map(([WarehouseID, WarehouseName]) => ({
        WarehouseID,
        WarehouseName,
      }))
      .sort((a, b) => a.WarehouseName.localeCompare(b.WarehouseName));
  }, [itemsWithIncomingStock]);

  const selectedWarehouseName = useMemo(() => {
    if (selectedWarehouse === "all") return "All Warehouses";

    const selected = warehouseOptions.find(
      (w) => String(w.WarehouseID) === String(selectedWarehouse)
    );

    return selected?.WarehouseName || "All Warehouses";
  }, [selectedWarehouse, warehouseOptions]);

  const chooseSupplier = (supplierId) => {
    setSelectedSupplier(supplierId);
    setSupplierDropdownOpen(false);
    setSupplierSearch("");
  };

  const filteredItems = useMemo(() => {
    const term = searchTerm.toLowerCase().trim();

    let arr = (itemsWithIncomingStock || []).filter((it) => {
      const matchesSearch =
        term === "" ||
        String(it.ItemName || "").toLowerCase().includes(term) ||
        String(it.CatalogNo || "").toLowerCase().includes(term) ||
        String(it.SupplierNames || "").toLowerCase().includes(term) ||
        String(it.SupplierLabel || "").toLowerCase().includes(term) ||
        String(it.WarehouseNames || "").toLowerCase().includes(term);

      const matchesWarehouse =
        selectedWarehouse === "all" ||
        String(it.WarehouseIDs || "")
          .split(",")
          .map((x) => x.trim())
          .includes(String(selectedWarehouse));

      if (selectedSupplier === "all") {
        return matchesSearch && matchesWarehouse;
      }

      const selected = suppliers.find(
        (s) => String(s.SupplierID) === String(selectedSupplier)
      );

      const selectedName = String(selected?.SupplierName || "")
        .toLowerCase()
        .trim();

      const matchesSupplier =
        selectedName &&
        String(it.SupplierNames || "")
          .toLowerCase()
          .split(",")
          .map((x) => x.trim())
          .includes(selectedName);

      return matchesSearch && matchesSupplier && matchesWarehouse;
    });

    arr.sort((a, b) => Number(b.TotalStock || 0) - Number(a.TotalStock || 0));
    return arr;
  }, [itemsWithIncomingStock, selectedSupplier, selectedWarehouse, searchTerm, suppliers]);

  useEffect(() => {
    setPage(1);
  }, [selectedSupplier, selectedWarehouse, searchTerm]);

  const totalPages = Math.max(1, Math.ceil(filteredItems.length / PAGE_SIZE));
  const pageSafe = Math.min(page, totalPages);
  const startIndex = (pageSafe - 1) * PAGE_SIZE;
  const pagedItems = filteredItems.slice(startIndex, startIndex + PAGE_SIZE);

  // const exportToExcelTemplate = () => {
  //   const reportData = filteredItems.map((it, index) => ({
  //     "No.": index + 1,
  //     "Catalog No": it.CatalogNo || "-",
  //     "Item Name": it.ItemName || "-",
  //     Suppliers: it.SupplierLabel || it.SupplierNames || "-",
  //     Warehouses: it.WarehouseNames || "-",
  //     "Total Stock": Number(it.TotalStock || 0),
  //   }));

  //   const wb = XLSX.utils.book_new();

  //   const wsData = [
  //     ["BIOCARE INVENTORY MANAGEMENT SYSTEM"],
  //     ["Stock Inventory Report"],
  //     [`Export Date: ${new Date().toLocaleString()}`],
  //     [`Supplier Filter: ${selectedSupplierName}`],
  //     [`Warehouse Filter: ${selectedWarehouseName}`],
  //     [`Search Filter: ${searchTerm || "None"}`],
  //     [],
  //     ["No.", "Catalog No", "Item Name", "Suppliers", "Warehouses", "Total Stock"],
  //     ...reportData.map((row) => [
  //       row["No."],
  //       row["Catalog No"],
  //       row["Item Name"],
  //       row["Suppliers"],
  //       row["Warehouses"],
  //       row["Total Stock"],
  //     ]),
  //   ];

  //   const ws = XLSX.utils.aoa_to_sheet(wsData);

  //   ws["!cols"] = [
  //     { wch: 8 },
  //     { wch: 20 },
  //     { wch: 40 },
  //     { wch: 40 },
  //     { wch: 35 },
  //     { wch: 15 },
  //   ];

  //   ws["!merges"] = [
  //     { s: { r: 0, c: 0 }, e: { r: 0, c: 5 } },
  //     { s: { r: 1, c: 0 }, e: { r: 1, c: 5 } },
  //     { s: { r: 2, c: 0 }, e: { r: 2, c: 5 } },
  //     { s: { r: 3, c: 0 }, e: { r: 3, c: 5 } },
  //     { s: { r: 4, c: 0 }, e: { r: 4, c: 5 } },
  //     { s: { r: 5, c: 0 }, e: { r: 5, c: 5 } },
  //   ];

  //   XLSX.utils.book_append_sheet(wb, ws, "Stock Report");

  //   const excelBuffer = XLSX.write(wb, {
  //     bookType: "xlsx",
  //     type: "array",
  //   });

  //   const fileData = new Blob([excelBuffer], {
  //     type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  //   });

  //   saveAs(
  //     fileData,
  //     `Stock_Report_${new Date().toISOString().slice(0, 10)}.xlsx`
  //   );
  // };

  const formatDateForExcel = (value) => {
    if (!value) return "-";

    const text = String(value);

    if (/^\d{4}-\d{2}-\d{2}/.test(text)) {
      return text.slice(0, 10);
    }

    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return text;

    return d.toISOString().slice(0, 10);
  };

  const exportToExcelTemplate = async () => {
    if (stockExporting) return;

    setStockExporting(true);

    try {
      const isRole2 = Number(roleId) === ROLE.SM;

      const wb = XLSX.utils.book_new();

      // ROLE 2: summary export only, no lot number, no expiry date
      if (isRole2) {
        const reportRows = filteredItems.map((it, index) => ({
          "No.": index + 1,
          "Catalog No": safeExcelText(it.CatalogNo),
          "Item Name": safeExcelText(it.ItemName),
          Suppliers: safeExcelText(it.SupplierLabel || it.SupplierNames),
          Warehouses: safeExcelText(it.WarehouseNames),
          "Total Stock": Number(it.TotalStock || 0),
          "Incoming Stock": Number(it.IncomingStock || 0),
        }));

        if (reportRows.length === 0) {
          alert("No stock records found to export.");
          return;
        }

        const wsData = [
          ["BIOCARE INVENTORY MANAGEMENT SYSTEM"],
          ["Stock Inventory Report"],
          [`Export Date: ${new Date().toLocaleString()}`],
          [`Supplier Filter: ${selectedSupplierName}`],
          [`Warehouse Filter: ${selectedWarehouseName}`],
          [`Search Filter: ${searchTerm || "None"}`],
          [],
          [
            "No.",
            "Catalog No",
            "Item Name",
            "Suppliers",
            "Warehouses",
            "Total Stock",
            "Incoming Stock",
          ],
          ...reportRows.map((row) => [
            row["No."],
            row["Catalog No"],
            row["Item Name"],
            row["Suppliers"],
            row["Warehouses"],
            row["Total Stock"],
            row["Incoming Stock"],
          ]),
        ];

        const ws = XLSX.utils.aoa_to_sheet(wsData);

        ws["!cols"] = [
          { wch: 8 },
          { wch: 20 },
          { wch: 45 },
          { wch: 40 },
          { wch: 35 },
          { wch: 15 },
          { wch: 16 },
        ];

        ws["!merges"] = [
          { s: { r: 0, c: 0 }, e: { r: 0, c: 6 } },
          { s: { r: 1, c: 0 }, e: { r: 1, c: 6 } },
          { s: { r: 2, c: 0 }, e: { r: 2, c: 6 } },
          { s: { r: 3, c: 0 }, e: { r: 3, c: 6 } },
          { s: { r: 4, c: 0 }, e: { r: 4, c: 6 } },
          { s: { r: 5, c: 0 }, e: { r: 5, c: 6 } },
        ];

        XLSX.utils.book_append_sheet(wb, ws, "Stock Report");
      } else {
        // Other roles: export with lot number and expiry date
        const reportRows = [];

        for (const it of filteredItems) {
          let lotRows = [];

          try {
            lotRows = await apiService.getItemDetailsByCatalog(it.CatalogNo);
          } catch (error) {
            console.error(`Failed to load lot details for ${it.CatalogNo}:`, error);
            lotRows = [];
          }

          if (Array.isArray(lotRows) && lotRows.length > 0) {
            lotRows.forEach((r) => {
              reportRows.push({
                "Catalog No": safeExcelText(it.CatalogNo),
                "Item Name": safeExcelText(it.ItemName),
                Suppliers: safeExcelText(it.SupplierLabel || it.SupplierNames),
                Warehouse: safeExcelText(r.WarehouseName),
                "Lot Number": safeExcelText(r.LotNumber),
                "Expiry Date": formatDateForExcel(r.ExpiryDate),
                "Days Remaining": r.ExpiryDate
                  ? getDaysRemainingPure(r.ExpiryDate)
                  : "-",
                "Total Stock": Number(r.TotalPieces || 0),
                "Incoming Stock": Number(it.IncomingStock || 0),
              });
            });
          } else {
            reportRows.push({
              "Catalog No": safeExcelText(it.CatalogNo),
              "Item Name": safeExcelText(it.ItemName),
              Suppliers: safeExcelText(it.SupplierLabel || it.SupplierNames),
              Warehouse: safeExcelText(it.WarehouseNames),
              "Lot Number": "-",
              "Expiry Date": "-",
              "Days Remaining": "-",
              "Total Stock": Number(it.TotalStock || 0),
              "Incoming Stock": Number(it.IncomingStock || 0),
            });
          }
        }

        if (reportRows.length === 0) {
          alert("No stock records found to export.");
          return;
        }

        const wsData = [
          ["BIOCARE INVENTORY MANAGEMENT SYSTEM"],
          ["Stock Inventory Report"],
          [`Export Date: ${new Date().toLocaleString()}`],
          [`Supplier Filter: ${selectedSupplierName}`],
          [`Warehouse Filter: ${selectedWarehouseName}`],
          [`Search Filter: ${searchTerm || "None"}`],
          [],
          [
            "No.",
            "Catalog No",
            "Item Name",
            "Suppliers",
            "Warehouse",
            "Lot Number",
            "Expiry Date",
            "Days Remaining",
            "Total Stock",
            "Incoming Stock",
          ],
          ...reportRows.map((row, index) => [
            index + 1,
            row["Catalog No"],
            row["Item Name"],
            row["Suppliers"],
            row["Warehouse"],
            row["Lot Number"],
            row["Expiry Date"],
            row["Days Remaining"],
            row["Total Stock"],
            row["Incoming Stock"],
          ]),
        ];

        const ws = XLSX.utils.aoa_to_sheet(wsData);

        ws["!cols"] = [
          { wch: 8 },
          { wch: 20 },
          { wch: 45 },
          { wch: 40 },
          { wch: 28 },
          { wch: 24 },
          { wch: 18 },
          { wch: 18 },
          { wch: 15 },
          { wch: 16 },
        ];

        ws["!merges"] = [
          { s: { r: 0, c: 0 }, e: { r: 0, c: 9 } },
          { s: { r: 1, c: 0 }, e: { r: 1, c: 9 } },
          { s: { r: 2, c: 0 }, e: { r: 2, c: 9 } },
          { s: { r: 3, c: 0 }, e: { r: 3, c: 9 } },
          { s: { r: 4, c: 0 }, e: { r: 4, c: 9 } },
          { s: { r: 5, c: 0 }, e: { r: 5, c: 9 } },
        ];

        XLSX.utils.book_append_sheet(wb, ws, "Stock Report");
      }

      const excelBuffer = XLSX.write(wb, {
        bookType: "xlsx",
        type: "array",
      });

      const fileData = new Blob([excelBuffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      saveAs(
        fileData,
        `Stock_Report_${new Date().toISOString().slice(0, 10)}.xlsx`
      );
    } catch (error) {
      console.error("Export stock summary error:", error);

      alert(
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message ||
        "Failed to export stock report."
      );
    } finally {
      setStockExporting(false);
    }
  };

  const groupMovementRows = (rows = []) => {
    const map = new Map();

    rows.forEach((r, index) => {
      const key = [
        r.TransactionDate || "",
        r.CatalogNo || "",
        r.ItemName || "",
        r.SupplierName || "",
        r.LotNumber || "",
        r.Action || "",
        r.PerformedBy || "",
      ].join("||");

      const stockIn = Number(r.StockIn || 0);
      const stockOut = Number(r.StockOut || 0);

      if (!map.has(key)) {
        map.set(key, {
          firstIndex: index,

          TransactionDate: r.TransactionDate || "-",
          CatalogNo: r.CatalogNo || "-",
          ItemName: r.ItemName || "-",
          SupplierName: r.SupplierName || "-",
          LotNumber: r.LotNumber || "-",
          Action: r.Action || "-",
          PerformedBy: r.PerformedBy || "-",

          PreviousQty: Number(r.PreviousQty || 0),
          StockIn: stockIn,
          StockOut: stockOut,
          BalanceQty: Number(r.BalanceQty || 0),

          BarcodeCount: r.Barcode ? 1 : 0,
          Barcodes: r.Barcode ? [String(r.Barcode)] : [],
        });

        return;
      }

      const existing = map.get(key);

      existing.StockIn += stockIn;
      existing.StockOut += stockOut;

      // Last balance after all rows inside same transaction group
      existing.BalanceQty = Number(r.BalanceQty || existing.BalanceQty || 0);

      if (r.Barcode) {
        existing.BarcodeCount += 1;
        existing.Barcodes.push(String(r.Barcode));
      }
    });

    return Array.from(map.values()).sort((a, b) => a.firstIndex - b.firstIndex);
  };

  const exportSelectedItemMovementExcel = async () => {
    if (!selectedItem) return;

    setMovementExporting(true);

    try {
      const rows = await apiService.getItemMovementReportByCatalog({
        catalogNo: selectedItem.CatalogNo,
        itemName: selectedItem.ItemName,
        from: movementFrom,
        to: movementTo,
        lotNumber: movementLotNumber,
      });

      const groupedRows = groupMovementRows(rows || []);

      const reportRows = groupedRows.map((r, index) => ({
        "No.": index + 1,
        "Transaction Date": safeExcelText(r.TransactionDate),
        "Catalog No": safeExcelText(r.CatalogNo),
        "Item Name": safeExcelText(r.ItemName),
        Supplier: safeExcelText(r.SupplierName),
        "Lot Number": safeExcelText(r.LotNumber),
        Action: safeExcelText(r.Action),
        "Previous Stock": Number(r.PreviousQty || 0),
        IN: Number(r.StockIn || 0),
        OUT: Number(r.StockOut || 0),
        "Balance Stock": Number(r.BalanceQty || 0),
        "Barcode Count": Number(r.BarcodeCount || 0),
        "Sample Barcodes": r.Barcodes?.slice(0, 10).join(", ") || "-",
        "Performed By": safeExcelText(r.PerformedBy),
      }));

      if (reportRows.length === 0) {
        alert("No movement record found for this item.");
        return;
      }

      const wb = XLSX.utils.book_new();

      const wsData = [
        ["BIOCARE INVENTORY MANAGEMENT SYSTEM"],
        ["Per Item Movement Report"],
        [`Item: ${selectedItem.ItemName || "-"}`],
        [`Catalog No: ${selectedItem.CatalogNo || "-"}`],
        [`Supplier: ${selectedItem.SupplierNames || selectedItem.SupplierLabel || "-"}`],
        [`Date From: ${movementFrom || "All"}`],
        [`Date To: ${movementTo || "All"}`],
        [`Lot Filter: ${movementLotNumber || "All"}`],
        [`Export Date: ${new Date().toLocaleString()}`],
        [],
        [
          "No.",
          "Transaction Date",
          "Catalog No",
          "Item Name",
          "Supplier",
          "Lot Number",
          "Action",
          "Previous Stock",
          "IN",
          "OUT",
          "Balance Stock",
          "Barcode Count",
          "Sample Barcodes",
          "Performed By",
        ],
        ...reportRows.map((row) => [
          row["No."],
          row["Transaction Date"],
          row["Catalog No"],
          row["Item Name"],
          row["Supplier"],
          row["Lot Number"],
          row["Action"],
          row["Previous Stock"],
          row["IN"],
          row["OUT"],
          row["Balance Stock"],
          row["Barcode Count"],
          row["Sample Barcodes"],
          row["Performed By"],
        ]),
      ];

      const ws = XLSX.utils.aoa_to_sheet(wsData);

      ws["!cols"] = [
        { wch: 8 },
        { wch: 22 },
        { wch: 20 },
        { wch: 45 },
        { wch: 35 },
        { wch: 22 },
        { wch: 20 },
        { wch: 16 },
        { wch: 10 },
        { wch: 10 },
        { wch: 16 },
        { wch: 15 },
        { wch: 60 },
        { wch: 24 },
      ];

      ws["!merges"] = [
        { s: { r: 0, c: 0 }, e: { r: 0, c: 13 } },
        { s: { r: 1, c: 0 }, e: { r: 1, c: 13 } },
        { s: { r: 2, c: 0 }, e: { r: 2, c: 13 } },
        { s: { r: 3, c: 0 }, e: { r: 3, c: 13 } },
        { s: { r: 4, c: 0 }, e: { r: 4, c: 13 } },
        { s: { r: 5, c: 0 }, e: { r: 5, c: 13 } },
        { s: { r: 6, c: 0 }, e: { r: 6, c: 13 } },
        { s: { r: 7, c: 0 }, e: { r: 7, c: 13 } },
        { s: { r: 8, c: 0 }, e: { r: 8, c: 13 } },
      ];

      XLSX.utils.book_append_sheet(wb, ws, "Item Movement");

      const excelBuffer = XLSX.write(wb, {
        bookType: "xlsx",
        type: "array",
      });

      const fileData = new Blob([excelBuffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      saveAs(
        fileData,
        `Item_Movement_${cleanFileName(selectedItem.CatalogNo)}_${new Date()
          .toISOString()
          .slice(0, 10)}.xlsx`
      );
    } catch (error) {
      console.error("Export selected item movement error:", error);

      alert(
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message ||
        "Failed to export selected item movement report."
      );
    } finally {
      setMovementExporting(false);
    }
  };

  const openItemModal = async (item) => {
    setSelectedItem(item);
    setShowModal(true);

    setDetails([]);
    setExpandedLotKey(null);
    setLotInfo(null);
    setExpandedBox(null);

    setMovementFrom("");
    setMovementTo("");
    setMovementLotNumber("");

    try {
      const rows = await apiService.getItemDetailsByCatalog(item.CatalogNo);
      setDetails(rows || []);
    } catch (e) {
      console.error("getItemDetailsByCatalog error:", e);
      setDetails([]);
    }
  };

  const openLotRow = async (row) => {
    if (!selectedItem) return;

    const key = `${row.WarehouseID}-${row.LotNumber}-${row.ExpiryDate || ""}`;
    const nextKey = expandedLotKey === key ? null : key;

    setExpandedLotKey(nextKey);
    setLotInfo(null);
    setExpandedBox(null);

    if (!nextKey) return;

    try {
      const barcodes = await apiService.getLotBarcodesByCatalog(
        selectedItem.CatalogNo,
        row.LotNumber,
        row.WarehouseID
      );

      const boxesIN = (barcodes.boxes || []).filter(
        (b) =>
          String(b.StockStatus || "IN").toUpperCase() === "IN" &&
          Number(b.Quantity || 0) > 0
      );

      const piecesIN = (barcodes.pieces || []).filter(
        (p) => String(p.StockStatus || "IN").toUpperCase() === "IN"
      );

      setLotInfo({
        lotNumber: row.LotNumber,
        itemName: selectedItem.ItemName,
        catalogNo: selectedItem.CatalogNo,
        receivedDate: row.ReceivedDate,
        expiryDate: row.ExpiryDate,
        warehouseID: row.WarehouseID,
        warehouseName: row.WarehouseName,
        supplierLabel: selectedItem.SupplierLabel,
        supplierNames: selectedItem.SupplierNames,
        totalStock: row.TotalPieces,
        boxes: boxesIN,
        pieces: piecesIN,
      });
    } catch (e) {
      console.error("getLotBarcodesByCatalog error:", e);
      setLotInfo(null);
    }
  };

  // Read-only annual count sheet. Child pieces are references, never extra stock.
  const printAnnualChecklist = async (itemToPrint, lotToPrint) => {
    if (annualPrinting) return;
    if (!itemToPrint || !lotToPrint) return alert("Select an item and lot first.");
    setAnnualPrinting(true);
    const started = new Date();
    const scope = { catalog: itemToPrint.CatalogNo, lot: lotToPrint.LotNumber, warehouse: lotToPrint.WarehouseName, expiry: formatDateForExcel(lotToPrint.ExpiryDate) };
    const supplierID = selectedSupplier;
    const warehouseID = lotToPrint.WarehouseID;
    const supplierName = suppliers.find((s) => String(s.SupplierID) === String(supplierID))?.SupplierName;
    try {
      const rows = [];
      const seen = new Set();
      const visited = new Set();
      for (const item of [itemToPrint]) {
        const lots = [lotToPrint];
        if (!Array.isArray(lots)) throw new Error("Invalid stock detail response.");
        for (const lot of lots) {
          if (warehouseID !== "all" && String(lot.WarehouseID) !== String(warehouseID)) continue;
          const key = JSON.stringify([item.CatalogNo, lot.LotNumber, lot.WarehouseID]);
          if (visited.has(key)) continue;
          visited.add(key);
          const data = await apiService.getLotBarcodesByCatalog(item.CatalogNo, lot.LotNumber, lot.WarehouseID);
          if (!Array.isArray(data?.boxes) || !Array.isArray(data?.pieces)) throw new Error("Invalid barcode response.");
          const add = (stock, type, qty) => {
            const id = String(stock.StockID ?? stock.Barcode);
            if (!stock.Barcode || seen.has(id)) return;
            seen.add(id);
            rows.push({ ...stock, type, qty, catalog: item.CatalogNo, item: stock.ItemName || item.ItemName,
              lot: lot.LotNumber, warehouse: lot.WarehouseName });
          };
          for (const box of data.boxes) {
            if (String(box.StockStatus).toUpperCase() !== "IN" || Number(box.Quantity) <= 0) continue;
            if (supplierID !== "all" && String(box.SupplierName).trim().toLowerCase() !== String(supplierName).trim().toLowerCase()) continue;
            add(box, "BOX", Number(box.Quantity));
            for (const piece of data.pieces.filter((p) => p.ParentBoxBarcode === box.Barcode && String(p.StockStatus).toUpperCase() === "IN")) {
              add(piece, "PIECE", 1);
            }
          }
        }
      }
      if (!rows.length) return alert("No available stock barcodes match the current filters.");
      const { jsPDF } = await import("jspdf");
      const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4", compress: true });
      const widths = [100, 70, 22, 85];
      const headers = ["Parent box / child barcode", "Item / catalog", "Type", "Location / lot / expiry / remarks"];
      // Keep each parent and its children together inside one table cell.
      const blocks = [];
      for (const box of rows.filter((row) => row.type === "BOX")) {
        const children = rows.filter((row) => row.ParentBoxBarcode === box.Barcode);
        for (let offset = 0; offset < Math.max(1, children.length); offset += 20) {
          const chunk = children.slice(offset, offset + 20);
          blocks.push({ box, children: chunk, continued: offset > 0,
            height: Math.max(16, 10 + chunk.length * 6) });
        }
      }
      const pageBlocks = [[]];
      let usedHeight = 0;
      for (const block of blocks) {
        if (usedHeight + block.height > 144) {
          pageBlocks.push([]);
          usedHeight = 0;
        }
        pageBlocks[pageBlocks.length - 1].push(block);
        usedHeight += block.height;
      }
      const pages = pageBlocks.length;
      const fit = (value, width, maxLines = 3) => {
        const lines = doc.splitTextToSize(String(value ?? "-"), width);
        if (lines.length > maxLines) throw new Error("A stock description is too long for the checklist. Shorten it before printing.");
        return lines;
      };
      for (let page = 0; page < pages; page++) {
        if (page) doc.addPage();
        doc.setTextColor(20, 40, 55);
        doc.setFont("helvetica", "bold"); doc.setFontSize(15);
        doc.text("BIOCARE | ANNUAL INVENTORY CHECKLIST", 10, 13);
        doc.setFont("helvetica", "normal"); doc.setFontSize(8);
        doc.text(`Year: ${started.getFullYear()}    Count date: __________________    Count team: __________________`, 10, 20);
        doc.text(fit(`Item: ${scope.catalog} | Lot: ${scope.lot || "-"} | Expiry date: ${scope.expiry} | Warehouse: ${scope.warehouse || "-"}`, 275, 2), 10, 26);
        let x = 10;
        doc.setFont("helvetica", "bold"); doc.setFontSize(7);
        headers.forEach((h, i) => { doc.setFillColor(230, 237, 241); doc.rect(x, 31, widths[i], 7, "FD"); doc.text(h, x + 1.5, 35.5); x += widths[i]; });
        doc.setFont("helvetica", "normal");
        let y = 38;
        pageBlocks[page].forEach(({ box, children, continued, height }) => {
          let left = 10;
          widths.forEach((w) => { doc.rect(left, y, w, height); left += w; });
          doc.setFontSize(7);
          doc.setFont("helvetica", "bold");
          if (!continued) doc.rect(13, y + 2.5, 3.5, 3.5);
          doc.text(fit(`${box.Barcode}${continued ? " (continued)" : ""}`, 86, 2), 20, y + 5.5);
          doc.setFont("helvetica", "normal");
          children.forEach((child, index) => {
            const childY = y + 12 + index * 6;
            doc.rect(20, childY - 2.5, 3.5, 3.5);
            doc.text(fit(child.Barcode, 76, 2), 27, childY);
          });
          doc.text(fit(`${box.catalog} / ${box.item}`, 67, 4), 111, y + 5);
          doc.text(children.length ? "BOX / PIECES" : "BOX", 181, y + 5);
          doc.setFontSize(6.5);
          doc.text(fit(`${box.warehouse || "-"} / Lot: ${box.lot || "-"} / Expiry: ${scope.expiry}`, 82, 3), 203, y + 5);
          doc.setFontSize(7);
          y += height;
        });
        doc.setFontSize(8);
        doc.text("Counted by: __________________    Verified by: __________________    Date: ______________", 10, 190);
        doc.setFontSize(7);
        doc.text(`Generated: ${started.toLocaleString()} - ${new Date().toLocaleTimeString()} | ${rows.length} barcode rows | Page ${page + 1} of ${pages}`, 10, 198);
      }
      doc.save(`Annual_Inventory_${cleanFileName(scope.catalog)}_Lot_${cleanFileName(scope.lot)}_${cleanFileName(scope.warehouse)}_${started.getFullYear()}.pdf`);
    } catch (error) {
      console.error("Annual inventory checklist failed:", error);
      alert(error?.message || "Could not generate the complete checklist. Please retry.");
    } finally {
      setAnnualPrinting(false);
    }
  };

  const drawBarcodePage = async (doc, barcodeText, LEFT, TOP) => {
    const value = String(barcodeText || "").toUpperCase();

    doc.setFontSize(6.5);
    doc.text(value, LEFT + 6, TOP + 2);

    const img = new Image();
    img.crossOrigin = "Anonymous";

    img.src =
      "https://bwipjs-api.metafloor.com/?" +
      "bcid=code128" +
      `&text=${encodeURIComponent(value)}` +
      "&scale=3" +
      "&height=10" +
      "&includetext=true" +
      "&parsefnc=true" +
      "&padding=6" +
      "&backgroundcolor=FFFFFF" +
      "&barcolor=000000";

    await new Promise((resolve) => {
      img.onload = () => {
        doc.addImage(img, "PNG", LEFT + 5, 5, 36, 6.5);
        resolve();
      };
      img.onerror = () => resolve();
    });
  };

  const handlePrintAllLabels = async (boxBarcode) => {
    const { jsPDF } = await import("jspdf");

    const doc = new jsPDF({
      orientation: "landscape",
      unit: "mm",
      format: [50, 15],
      compress: true,
    });

    const LEFT = 2;
    const TOP = 2;

    await drawBarcodePage(doc, boxBarcode, LEFT, TOP);

    const pieces =
      lotInfo?.pieces?.filter((p) => p.ParentBoxBarcode === boxBarcode) || [];

    for (let i = 0; i < pieces.length; i++) {
      doc.addPage();
      await drawBarcodePage(doc, pieces[i].Barcode, LEFT, TOP);
    }

    doc.save(`${boxBarcode}_ALL.pdf`);
  };

  const safe = (s) => String(s || "").replace(/[^\w-]+/g, "_");

  const handlePrintWholeLotLabels = async (receivedDateFilter = null) => {
    if (!lotInfo) return;

    const BATCH_SIZE = 200;

    const normalizeDate = (value) =>
      String(value || lotInfo.receivedDate || "NO_DATE").slice(0, 10);

    const selectedBoxes = (lotInfo.boxes || []).filter((box) => {
      if (!receivedDateFilter) return true;

      const boxDate = normalizeDate(box.ReceivedDate);
      return String(boxDate) === String(receivedDateFilter).slice(0, 10);
    });

    if (selectedBoxes.length === 0) {
      alert("No barcodes found for this received date.");
      return;
    }

    const selectedBoxSet = new Set(
      selectedBoxes.map((box) => String(box.Barcode || ""))
    );

    const selectedPieces = (lotInfo.pieces || []).filter((piece) =>
      selectedBoxSet.has(String(piece.ParentBoxBarcode || ""))
    );

    const labelList = [];

    selectedBoxes.forEach((box) => {
      if (box.Barcode) {
        labelList.push({
          type: "BOX",
          barcode: box.Barcode,
        });
      }

      const childPieces = selectedPieces.filter(
        (piece) =>
          String(piece.ParentBoxBarcode || "") === String(box.Barcode || "")
      );

      childPieces.forEach((piece) => {
        if (piece.Barcode) {
          labelList.push({
            type: "PC",
            barcode: piece.Barcode,
          });
        }
      });
    });

    if (labelList.length === 0) {
      alert("No barcodes found for this received date.");
      return;
    }

    const totalBatches = Math.ceil(labelList.length / BATCH_SIZE);

    setBarcodePrintModal({
      open: true,
      labels: labelList,
      receivedDateFilter,
      totalLabels: labelList.length,
      totalBatches,
      loading: false,
    });
  };

  const confirmPrintWholeLotLabels = async () => {
    if (!lotInfo || barcodePrintModal.labels.length === 0) return;

    const BATCH_SIZE = 200;

    setBarcodePrintModal((prev) => ({
      ...prev,
      loading: true,
    }));

    try {
      const { jsPDF } = await import("jspdf");

      const LEFT = 2;
      const TOP = 2;
      const labelList = barcodePrintModal.labels;
      const totalBatches = barcodePrintModal.totalBatches;
      const receivedDateFilter = barcodePrintModal.receivedDateFilter;

      for (let batchIndex = 0; batchIndex < totalBatches; batchIndex++) {
        const batchLabels = labelList.slice(
          batchIndex * BATCH_SIZE,
          (batchIndex + 1) * BATCH_SIZE
        );

        const doc = new jsPDF({
          orientation: "landscape",
          unit: "mm",
          format: [50, 15],
          compress: true,
        });

        for (let i = 0; i < batchLabels.length; i++) {
          if (i > 0) {
            doc.addPage();
          }

          await drawBarcodePage(doc, batchLabels[i].barcode, LEFT, TOP);
        }

        const filename = `${safe(lotInfo.catalogNo)}_${safe(
          lotInfo.lotNumber
        )}_${safe(lotInfo.warehouseName)}_${safe(
          receivedDateFilter || lotInfo.receivedDate || "ALL_DATES"
        )}_BATCH_${batchIndex + 1}_OF_${totalBatches}.pdf`;

        doc.save(filename);

        await new Promise((resolve) => setTimeout(resolve, 500));
      }

      setBarcodePrintModal({
        open: false,
        labels: [],
        receivedDateFilter: null,
        totalLabels: 0,
        totalBatches: 0,
        loading: false,
      });
    } catch (error) {
      console.error("Barcode PDF generation error:", error);

      setBarcodePrintModal((prev) => ({
        ...prev,
        loading: false,
      }));

      alert("Failed to generate barcode PDF. Please try smaller batch.");
    }
  };

  const closeBarcodePrintModal = () => {
    if (barcodePrintModal.loading) return;

    setBarcodePrintModal({
      open: false,
      labels: [],
      receivedDateFilter: null,
      totalLabels: 0,
      totalBatches: 0,
      loading: false,
    });
  };

  const downloadCSV = (filename, rows) => {
    const blob = new Blob([rows.join("\n")], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const downloadBoxBarcodes = (boxBarcode) => {
    if (!lotInfo) return;

    const pieces = (lotInfo.pieces || []).filter(
      (p) => p.ParentBoxBarcode === boxBarcode
    );

    const lines = [
      "Type,Barcode,ParentBoxBarcode",
      `BOX,${boxBarcode},`,
      ...pieces.map((p) => `PC,${p.Barcode},${p.ParentBoxBarcode}`),
    ];

    const filename = `${safe(lotInfo.catalogNo)}_${safe(
      lotInfo.lotNumber
    )}_${safe(lotInfo.warehouseName)}_${safe(boxBarcode)}.csv`;

    downloadCSV(filename, lines);
  };

  const downloadBarcodesByReceivedDate = (receivedDateFilter) => {
    if (!lotInfo) return;

    const boxes = (lotInfo.boxes || []).filter((b) => {
      const receivedDate = String(
        b.ReceivedDate || lotInfo.receivedDate || "NO_DATE"
      ).slice(0, 10);

      return String(receivedDate) === String(receivedDateFilter).slice(0, 10);
    });

    const selectedBoxSet = new Set(boxes.map((b) => String(b.Barcode || "")));

    const pieces = (lotInfo.pieces || []).filter((p) =>
      selectedBoxSet.has(String(p.ParentBoxBarcode || ""))
    );

    const lines = ["Type,Barcode,ParentBoxBarcode,ReceivedDate"];

    boxes.forEach((b) => {
      const receivedDate = b.ReceivedDate || lotInfo.receivedDate || "";

      lines.push(`BOX,${b.Barcode},,${receivedDate}`);

      const childPieces = pieces.filter(
        (p) => String(p.ParentBoxBarcode || "") === String(b.Barcode || "")
      );

      childPieces.forEach((p) => {
        lines.push(
          `PC,${p.Barcode},${p.ParentBoxBarcode},${p.ReceivedDate || receivedDate
          }`
        );
      });
    });

    const filename = `${safe(lotInfo.catalogNo)}_${safe(
      lotInfo.lotNumber
    )}_${safe(lotInfo.warehouseName)}_${safe(
      receivedDateFilter
    )}_BARCODES.csv`;

    downloadCSV(filename, lines);
  };

  const goPrev = () => setPage((p) => Math.max(1, p - 1));
  const goNext = () => setPage((p) => Math.min(totalPages, p + 1));
  const goTo = (n) => setPage(() => Math.min(totalPages, Math.max(1, n)));

  const isPieceOnlyItem = useMemo(() => {
    if (!details || details.length === 0) return false;
    return details.every((d) => Number(d.PiecesPerBox || 1) <= 1);
  }, [details]);

  const barcodeGroupsByReceivedDate = useMemo(() => {
    if (!lotInfo) return [];

    const map = new Map();

    const getDate = (row) =>
      String(row?.ReceivedDate || lotInfo.receivedDate || "NO_DATE").slice(
        0,
        10
      );

    (lotInfo.boxes || []).forEach((box) => {
      const date = getDate(box);

      if (!map.has(date)) {
        map.set(date, {
          date,
          boxes: [],
        });
      }

      map.get(date).boxes.push(box);
    });

    return Array.from(map.values()).sort((a, b) =>
      String(a.date).localeCompare(String(b.date))
    );
  }, [lotInfo]);

  return (
    <div className="stocklist-page">
      <div className="stocklist-shell">
        <div className="stocklist-topbar">
          <div>
            <p className="stocklist-eyebrow">Inventory Management</p>
            <h1 className="stocklist-title">Stock Inventory</h1>
            <p className="stocklist-subtitle">
              Monitor item stock, suppliers, warehouse lots, and barcode-level
              details.
            </p>
          </div>

          <div className="stocklist-summary-card">
            <span className="summary-label">Visible Items</span>
            <strong className="summary-value">{filteredItems.length}</strong>
          </div>
        </div>

        <div className="stocklist-filters-card">
          <div className="filters-row">
            <div
              className="filter-group supplier-search-group"
              ref={supplierDropdownRef}
            >
              <label>Supplier</label>

              <div
                className={`supplier-combobox ${supplierDropdownOpen ? "open" : ""
                  }`}
              >
                <button
                  type="button"
                  className="supplier-combobox-trigger"
                  onClick={() => {
                    setSupplierDropdownOpen((prev) => !prev);
                    setSupplierSearch("");
                  }}
                >
                  <span>{selectedSupplierName}</span>
                  <span className="supplier-combobox-arrow">▾</span>
                </button>

                {supplierDropdownOpen && (
                  <div className="supplier-combobox-menu">
                    <input
                      type="text"
                      className="supplier-combobox-search"
                      placeholder="Search supplier..."
                      value={supplierSearch}
                      autoFocus
                      onChange={(e) => setSupplierSearch(e.target.value)}
                    />

                    <button
                      type="button"
                      className={`supplier-option ${selectedSupplier === "all" ? "active" : ""
                        }`}
                      onClick={() => chooseSupplier("all")}
                    >
                      All Suppliers
                    </button>

                    <div className="supplier-options-list">
                      {filteredSupplierOptions.length === 0 ? (
                        <div className="supplier-empty">No supplier found.</div>
                      ) : (
                        filteredSupplierOptions.map((s) => (
                          <button
                            type="button"
                            key={s.SupplierID}
                            className={`supplier-option ${String(selectedSupplier) === String(s.SupplierID)
                              ? "active"
                              : ""
                              }`}
                            onClick={() => chooseSupplier(String(s.SupplierID))}
                          >
                            {s.SupplierName}
                          </button>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="filter-group warehouse-filter-group">
              <label>Warehouse</label>
              <select
                value={selectedWarehouse}
                onChange={(e) => setSelectedWarehouse(e.target.value)}
              >
                <option value="all">All Warehouses</option>

                {warehouseOptions.map((w) => (
                  <option key={w.WarehouseID} value={w.WarehouseID}>
                    {w.WarehouseName}
                  </option>
                ))}
              </select>
            </div>

            <div className="filter-group search-area">
              <label>Search</label>
              <input
                type="text"
                placeholder="Search by item, catalog no, supplier, or warehouse"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          <div className="filter-group export-btn">
            {/* <button className="primary-btn" onClick={exportToExcelTemplate}>
              Export Stock Summary
            </button> */}
            <button
              className="primary-btn"
              onClick={exportToExcelTemplate}
              disabled={stockExporting}
            >
              {stockExporting ? "Exporting..." : "Export Stock Summary"}
            </button>
          </div>
        </div>

        <div className="table-card">
          <div className="table-card-header">
            <h2>Stock Overview</h2>
            <span className="table-badge">
              Showing {filteredItems.length === 0 ? 0 : startIndex + 1}-
              {Math.min(startIndex + PAGE_SIZE, filteredItems.length)} of{" "}
              {filteredItems.length}
            </span>
          </div>

          <div className="table-wrap">
            <table className="stock-table">
              <thead>
                <tr>
                  <th>Catalog No</th>
                  <th>Description</th>
                  <th>Suppliers</th>
                  <th>Warehouses</th>
                  <th>Total Stock</th>
                  <th>Incoming Stock</th>
                </tr>
              </thead>

              <tbody>
                {pagedItems.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="empty-state">
                      No items found.
                    </td>
                  </tr>
                ) : (
                  pagedItems.map((it) => (
                    <tr
                      key={`${it.CatalogNo}-${it.ItemName}`}
                      onClick={() => openItemModal(it)}
                      className="clickable-row"
                    >
                      <td>
                        <span className="catalog-chip">
                          {it.CatalogNo || "-"}
                        </span>
                      </td>
                      <td>{it.ItemName}</td>
                      <td>{it.SupplierLabel || it.SupplierNames || "-"}</td>
                      <td>{it.WarehouseNames || "-"}</td>
                      <td>
                        <strong className="stock-number">
                          {Number(it.TotalStock || 0)}
                        </strong>
                      </td>
                      <td>
                        <strong className="stock-number incoming-stock-number">
                          {Number(it.IncomingStock || 0)}
                        </strong>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="pagination-row">
            <div className="page-actions">
              <button onClick={goPrev} disabled={pageSafe <= 1}>
                ◀ Prev
              </button>
              <span className="page-info">
                Page {pageSafe} of {totalPages}
              </span>
              <button onClick={goNext} disabled={pageSafe >= totalPages}>
                Next ▶
              </button>
            </div>

            <span className="page-count">
              Showing {filteredItems.length === 0 ? 0 : startIndex + 1}-
              {Math.min(startIndex + PAGE_SIZE, filteredItems.length)} of{" "}
              {filteredItems.length}
            </span>

            <div className="jump">
              <span>Go to:</span>
              <input
                type="number"
                min={1}
                max={totalPages}
                value={pageSafe}
                onChange={(e) => goTo(Number(e.target.value || 1))}
              />
            </div>
          </div>
        </div>
      </div>

      {showModal && selectedItem && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-box" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <p className="modal-label">Item Details</p>
                <h3>
                  {selectedItem.ItemName}{" "}
                  <span className="modal-catalog">
                    ({selectedItem.CatalogNo})
                  </span>
                </h3>
              </div>

              <button
                className="close-icon-btn"
                onClick={() => setShowModal(false)}
              >
                ✕
              </button>
            </div>

            <div className="modal-summary-grid">
              <div className="info-card">
                <span className="info-label">Suppliers</span>
                <strong>
                  {selectedItem.SupplierNames ||
                    selectedItem.SupplierLabel ||
                    "-"}
                </strong>
              </div>

              <div className="info-card">
                <span className="info-label">Warehouses</span>
                <strong>{selectedItem.WarehouseNames || "-"}</strong>
              </div>

              <div className="info-card">
                <span className="info-label">Total Stock</span>
                <strong>{Number(selectedItem.TotalStock || 0)}</strong>
              </div>

              <div className="info-card">
                <span className="info-label">Incoming Stock</span>
                <strong>{Number(selectedItem.IncomingStock || 0)}</strong>
              </div>
            </div>

            <div className="stocklist-filters-card item-movement-export-card">
              <div className="filters-row">
                <div className="filter-group warehouse-filter-group">
                  <label>Movement From</label>
                  <input
                    type="date"
                    value={movementFrom}
                    onChange={(e) => setMovementFrom(e.target.value)}
                  />
                </div>

                <div className="filter-group warehouse-filter-group">
                  <label>Movement To</label>
                  <input
                    type="date"
                    value={movementTo}
                    onChange={(e) => setMovementTo(e.target.value)}
                  />
                </div>

                <div className="filter-group search-area">
                  <label>Lot Number</label>
                  <input
                    type="text"
                    placeholder="Optional lot filter"
                    value={movementLotNumber}
                    onChange={(e) => setMovementLotNumber(e.target.value)}
                  />
                </div>
              </div>

              <div className="filter-group export-btn">
                <button
                  className="secondary-btn"
                  onClick={exportSelectedItemMovementExcel}
                  disabled={movementExporting}
                >
                  {movementExporting
                    ? "Exporting..."
                    : "Export This Item Movement"}
                </button>
              </div>
            </div>

            {details.length === 0 ? (
              <div className="empty-modal-state">No stock available.</div>
            ) : (
              <div className="table-wrap modal-table-wrap">
                <table className="stock-table">
                  <thead>
                    <tr>
                      <th>Warehouse</th>
                      <th>Lot</th>
                      {showExpiryColumns && <th>Expiry Date</th>}
                      {showExpiryColumns && <th>Days Remaining</th>}
                      <th>Boxes</th>
                      <th>Vial BOX/KIT</th>
                      <th>{isPieceOnlyItem ? "Total" : "Total Vial"}</th>
                    </tr>
                  </thead>

                  <tbody>
                    {details.map((r, idx) => {
                      const showExpiryValue = canShowExpiryValue(
                        roleId,
                        r.ExpiryDate
                      );

                      const expDisplay =
                        showExpiryColumns && showExpiryValue
                          ? r.ExpiryDate || "-"
                          : "—";

                      const days = getDaysRemainingPure(r.ExpiryDate);

                      const daysDisplay =
                        showExpiryColumns && showExpiryValue
                          ? `${days} days`
                          : "—";

                      const rowKey = `${r.WarehouseID}-${r.LotNumber}-${r.ExpiryDate || ""
                        }`;

                      const expanded = expandedLotKey === rowKey;

                      const boxes = Number(r.BoxCount || 0);
                      const ppb = Number(r.PiecesPerBox || 1);
                      const isPieceOnly = ppb <= 1;
                      const totalDisplay = Number(r.TotalPieces || 0);

                      return (
                        <React.Fragment key={rowKey || idx}>
                          <tr
                            className={`${showExpiryColumns
                              ? getExpiryStatus(r.ExpiryDate)
                              : ""
                              } clickable-row`}
                            onClick={() => openLotRow(r)}
                          >
                            <td>{r.WarehouseName || "-"}</td>
                            <td>{r.LotNumber || "-"}</td>
                            {showExpiryColumns && <td>{expDisplay}</td>}
                            {showExpiryColumns && <td>{daysDisplay}</td>}
                            <td>{boxes}</td>
                            <td>{isPieceOnly ? "—" : ppb}</td>
                            <td>
                              <strong className="stock-number">
                                {totalDisplay}
                              </strong>
                            </td>
                          </tr>

                          {expanded && lotInfo && (
                            <tr>
                              <td colSpan={showExpiryColumns ? 7 : 5}>
                                <div className="barcode-date-section">
                                  <section className="annual-count-card" aria-label="Annual inventory checklist">
                                    <div className="annual-count-icon" aria-hidden="true">
                                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                                        <rect x="5" y="4" width="14" height="17" rx="2" />
                                        <rect x="9" y="2" width="6" height="4" rx="1" />
                                        <path d="m8 11 1.5 1.5L12 10M14 11h2M8 17h8" />
                                      </svg>
                                    </div>
                                    <div className="annual-count-content">
                                      <h4 className="annual-count-title">Annual Inventory Checklist</h4>
                                      <div className="annual-count-meta">
                                        <span><span className="annual-count-label">Catalog</span><strong>{selectedItem.CatalogNo}</strong></span>
                                        <span><span className="annual-count-label">Lot</span><strong>{r.LotNumber || "-"}</strong></span>
                                        <span><span className="annual-count-label">Warehouse</span><strong>{r.WarehouseName || "-"}</strong></span>
                                      </div>
                                    </div>
                                    <button type="button" className="annual-count-print"
                                      onClick={() => printAnnualChecklist(selectedItem, r)}
                                      disabled={annualPrinting} aria-busy={annualPrinting}>
                                      <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M6 9V3h12v6M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
                                        <path d="M6 14h12v7H6zM18 12h.01" />
                                      </svg>
                                      {annualPrinting ? "Preparing checklist..." : "Print Lot Checklist"}
                                    </button>
                                  </section>
                                  {barcodeGroupsByReceivedDate.map((group) => (
                                    <div
                                      key={group.date}
                                      className="received-date-group"
                                    >
                                      <div className="received-date-header">
                                        <div>
                                          <h4>
                                            Received Date:{" "}
                                            {group.date === "NO_DATE"
                                              ? "No Date"
                                              : group.date}
                                          </h4>
                                          <p>
                                            {group.boxes.length} box
                                            {group.boxes.length > 1
                                              ? "es"
                                              : ""}{" "}
                                            under this date
                                          </p>
                                        </div>

                                        <div className="received-date-actions">
                                          <button
                                            className="primary-btn btn-barcode"
                                            onClick={() =>
                                              handlePrintWholeLotLabels(
                                                group.date
                                              )
                                            }
                                          >
                                            Print this date
                                          </button>

                                          <button
                                            className="secondary-btn btn-barcode"
                                            onClick={() =>
                                              downloadBarcodesByReceivedDate(
                                                group.date
                                              )
                                            }
                                          >
                                            Download this date
                                          </button>
                                        </div>
                                      </div>

                                      <div className="barcode-section">
                                        {group.boxes.map((b) => {
                                          const pieces =
                                            lotInfo?.pieces?.filter(
                                              (p) =>
                                                String(p.ParentBoxBarcode) ===
                                                String(b.Barcode)
                                            ) || [];

                                          const isExpanded =
                                            expandedBox === b.Barcode;

                                          return (
                                            <div
                                              className="barcode-card"
                                              key={b.StockID}
                                            >
                                              <div
                                                className="box-click"
                                                onClick={() =>
                                                  setExpandedBox(
                                                    isExpanded
                                                      ? null
                                                      : b.Barcode
                                                  )
                                                }
                                              >
                                                <div>
                                                  <strong>{b.Barcode}</strong>

                                                  <span className="box-piece-count">
                                                    {pieces.length} pcs
                                                  </span>

                                                  <span className="barcode-received-date">
                                                    Received:{" "}
                                                    {b.ReceivedDate ||
                                                      lotInfo.receivedDate ||
                                                      "-"}
                                                  </span>
                                                </div>

                                                <span className="arrow">
                                                  {isExpanded ? "▲" : "▼"}
                                                </span>
                                              </div>

                                              {isExpanded && (
                                                <div className="piece-slide">
                                                  <div className="piece-toolbar">
                                                    <button
                                                      className="btn-barcode primary-btn"
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        handlePrintAllLabels(
                                                          b.Barcode
                                                        );
                                                      }}
                                                    >
                                                      Print Box
                                                    </button>

                                                    <button
                                                      className="btn-barcode secondary-btn"
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        downloadBoxBarcodes(
                                                          b.Barcode
                                                        );
                                                      }}
                                                    >
                                                      Download CSV
                                                    </button>
                                                  </div>

                                                  <div className="piece-list">
                                                    {pieces.map((p) => (
                                                      <div
                                                        key={p.StockID}
                                                        className="piece-item"
                                                      >
                                                        <span>{p.Barcode}</span>
                                                        <small>
                                                          Received:{" "}
                                                          {p.ReceivedDate ||
                                                            b.ReceivedDate ||
                                                            lotInfo.receivedDate ||
                                                            "-"}
                                                        </small>
                                                      </div>
                                                    ))}
                                                  </div>
                                                </div>
                                              )}
                                            </div>
                                          );
                                        })}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            <div className="modal-footer">
              <button className="close-btn" onClick={() => setShowModal(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {barcodePrintModal.open && (
        <div
          className="barcode-confirm-overlay"
          onClick={closeBarcodePrintModal}
        >
          <div
            className="barcode-confirm-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="barcode-confirm-header">
              <div className="barcode-confirm-icon-wrap">
                <span className="barcode-confirm-icon">!</span>
              </div>

              <button
                type="button"
                className="barcode-confirm-close"
                onClick={closeBarcodePrintModal}
                disabled={barcodePrintModal.loading}
              >
                ×
              </button>
            </div>

            <div className="barcode-confirm-body">
              <p className="barcode-confirm-label">Barcode PDF Generation</p>

              <h3>Generate barcode labels?</h3>

              <p className="barcode-confirm-desc">
                The system will split the barcode labels into smaller PDF files
                to prevent the browser from freezing.
              </p>

              <div className="barcode-confirm-stats">
                <div className="barcode-confirm-stat">
                  <span>Total Labels</span>
                  <strong>{barcodePrintModal.totalLabels}</strong>
                </div>

                <div className="barcode-confirm-stat">
                  <span>PDF Files</span>
                  <strong>{barcodePrintModal.totalBatches}</strong>
                </div>

                <div className="barcode-confirm-stat">
                  <span>Labels per PDF</span>
                  <strong>200</strong>
                </div>
              </div>

              <div className="barcode-confirm-note">
                Please do not refresh or close this page while the PDFs are
                being generated.
              </div>
            </div>

            <div className="barcode-confirm-actions">
              <button
                type="button"
                className="barcode-confirm-cancel"
                onClick={closeBarcodePrintModal}
                disabled={barcodePrintModal.loading}
              >
                Cancel
              </button>

              <button
                type="button"
                className="barcode-confirm-ok"
                onClick={confirmPrintWholeLotLabels}
                disabled={barcodePrintModal.loading}
              >
                {barcodePrintModal.loading ? (
                  <>
                    <span className="barcode-confirm-spinner"></span>
                    Generating...
                  </>
                ) : (
                  "Generate PDFs"
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}